import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { prop, sortBy } from 'remeda';
import { Records } from 'src/graphql/types';
import type { ConversationUrgencyRaw } from 'src/types';

const GET_ENABLED_CONVERSATION_URGENCIES_QUERY = gql`
  query GetEnabledConversationUrgencies($partnerId: Int) @entity(name: "ConversationUrgency") {
    getEnabledConversationUrgencies(partnerId: $partnerId) {
      records {
        id
        urgencyName
        sortOrder
      }
    }
  }
`;

interface GetEnabledConversationUrgenciesQuery {
  getEnabledConversationUrgencies: Records<ConversationUrgencyRaw>;
}

export interface ConversationUrgency {
  id: BaseConversationUrgency | string;
  name: ConversationUrgencyRaw['urgencyName'];
  sortOrder: number;
}

export const useConversationUrgencyList = (
  partnerId?: number,
  options?: QueryHookOptions<GetEnabledConversationUrgenciesQuery>,
) => {
  const { data, ...rest } = useQuery<GetEnabledConversationUrgenciesQuery>(
    GET_ENABLED_CONVERSATION_URGENCIES_QUERY,
    {
      ...options,
      context: {
        omitIsolationModeHeader: true,
      },
      variables: {
        partnerId,
      },
    },
  );
  const conversationUrgencyList = useMemo(() => {
    if (!data?.getEnabledConversationUrgencies?.records?.length) {
      return null;
    }

    return data?.getEnabledConversationUrgencies.records.map(
      (conversationUrgency: ConversationUrgencyRaw) => {
        return {
          id: conversationUrgency.id,
          name: conversationUrgency.urgencyName,
          sortOrder: conversationUrgency.sortOrder,
        };
      },
    );
  }, [data?.getEnabledConversationUrgencies.records]);

  return {
    conversationUrgencyList,
    ...rest,
  };
};
export function useConversationUrgencyOptions(
  partnerId?: number,
  options?: QueryHookOptions<GetEnabledConversationUrgenciesQuery>,
) {
  const { conversationUrgencyList } = useConversationUrgencyList(partnerId, options);
  return useMemo(() => {
    if (!conversationUrgencyList) return [];
    return sortBy(
      conversationUrgencyList.map((item) => ({
        id: item.id,
        value: item.id,
        label: item.name,
        sortOrder: item.sortOrder,
        icon: getUrgencyIcon(item.id),
        color: getUrgencyColor(item.id),
      })),
      prop('sortOrder'),
    );
  }, [conversationUrgencyList]);
}

export enum BaseConversationUrgency {
  Low = 'low',
  Normal = 'normal',
  Urgent = 'urgent',
  LifeSafety = 'life_safety',
}

export function getUrgencyIcon<T extends string>(urgency: T): string {
  switch (urgency) {
    case BaseConversationUrgency.Low:
      return 'signal_cellular_1_bar';
    case BaseConversationUrgency.Normal:
      return 'signal_cellular_3_bar';
    case BaseConversationUrgency.Urgent:
      return 'warning';
    case BaseConversationUrgency.LifeSafety:
      return 'e911_emergency';
    default:
      return 'signal_cellular_3_bar';
  }
}

export function getUrgencyColor<T extends string>(urgency: T): string {
  switch (urgency) {
    case BaseConversationUrgency.Low:
      return 'grey.500';
    case BaseConversationUrgency.Normal:
      return 'custom.tertiary.tertiary60';
    case BaseConversationUrgency.Urgent:
      return 'warning.main';
    case BaseConversationUrgency.LifeSafety:
      return 'error.main';
    default:
      return 'text.secondary';
  }
}
